/**
 * **A movement's trend on a task card** — the client's history on one movement read on its primary
 * line (`model/liftTrend`), beside a clip on the `/zadania` desk and under a strength goal (owner,
 * 26 September 2026). The same records read the Forma tab draws, whole history, one cache entry.
 */

import { useTranslation } from 'react-i18next';

import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { TrendCard } from '~/shared/ui/chart/TrendCard';

import { useLiftHistory } from '../lib/useLiftHistory';
import { liftTrend } from '../model/liftTrend';

interface LiftTrendChartProps {
  readonly clientId: number;
  readonly exerciseId: number;
  readonly reducedMotion: boolean;
}

export function LiftTrendChart({ clientId, exerciseId, reducedMotion }: LiftTrendChartProps) {
  const { t, i18n } = useTranslation();
  const history = useLiftHistory(clientId, exerciseId);
  const gate = useSkeletonGate(history.pending);
  const trend =
    history.pending || history.failed ? null : liftTrend(history.sessions, history.fields, { t, locale: i18n.language });

  return (
    <TrendCard
      title={trend === null ? t('web:forma.trend.bare') : t('web:forma.trend.title', { line: trend.name })}
      gate={gate}
      failed={history.failed}
      failedTitle={t('web:forma.trend.failed')}
      emptyTitle={t('web:forma.trend.empty')}
      series={trend}
      reducedMotion={reducedMotion}
    />
  );
}
